
import { UnifiedStratigraphy } from './types';
import { WallType } from '@/types';

interface FilterOptions {
  wallType: WallType | 'all';
  searchTerm: string;
  materialSearchTerm: string;
  fireResistance: string;
  acousticReduction: string;
  supplierName: string;
  thicknessRange: string;
  /** Range peso kg/m². Es. "0-25", "25-40", "40-60", "60-80", "80+". 'all' o vuoto = no filter. */
  weightRange?: string;
  showCertifiedOnly: boolean;
  showCustomOnly: boolean;
}

export const filterStratigraphies = (
  stratigraphies: UnifiedStratigraphy[],
  filters: FilterOptions
): UnifiedStratigraphy[] => {
  return stratigraphies.filter(stratigraphy => {
    // Wall type filter.
    // Convenzione: wallType='all' significa "tutte le pareti" → ESCLUDE i ceiling.
    // La lista controsoffitti chiama esplicitamente con wallType='ceiling'.
    // Senza questa esclusione i ceiling SG (CS.AUT.ASF, CS.AN.AUT, ...) comparivano
    // anche nel configuratore pareti — bug segnalato il 2026-05-25.
    if (filters.wallType === 'all') {
      if (stratigraphy.type === 'ceiling') return false;
    } else if (stratigraphy.type !== filters.wallType) {
      return false;
    }

    // Search term filter
    if (filters.searchTerm && !stratigraphy.name.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
      return false;
    }

    // Material search filter
    if (filters.materialSearchTerm) {
      const hasMatchingMaterial = stratigraphy.layers?.some(layer => 
        layer.material?.name?.toLowerCase().includes(filters.materialSearchTerm.toLowerCase())
      );
      if (!hasMatchingMaterial) return false;
    }

    // Fire resistance filter — "all" = nessun filtro (i Select shadcn non
    // accettano value="" quindi usiamo "all" come sentinel di "tutto").
    if (
      filters.fireResistance &&
      filters.fireResistance !== 'all' &&
      stratigraphy.fire_resistance_class !== filters.fireResistance
    ) {
      return false;
    }

    // Acoustic reduction filter
    if (filters.acousticReduction && filters.acousticReduction !== 'all') {
      const minAcoustic = parseInt(filters.acousticReduction);
      if (!stratigraphy.acoustic_performance || stratigraphy.acoustic_performance < minAcoustic) {
        return false;
      }
    }

    // Supplier filter — match parziale case-insensitive ("saint" trova "Saint Gobain")
    if (filters.supplierName) {
      const supplier = (stratigraphy.supplier_name ?? '').toLowerCase();
      if (!supplier.includes(filters.supplierName.toLowerCase())) {
        return false;
      }
    }

    // Thickness range filter
    if (filters.thicknessRange && filters.thicknessRange !== 'all') {
      // Supporta sia "X-Y" (range) sia "X+" (open-ended verso l'alto)
      if (filters.thicknessRange.endsWith('+')) {
        const min = Number(filters.thicknessRange.replace('+', ''));
        if (stratigraphy.total_thickness < min) return false;
      } else {
        const [min, max] = filters.thicknessRange.split('-').map(Number);
        if (stratigraphy.total_thickness < min || (max && stratigraphy.total_thickness > max)) {
          return false;
        }
      }
    }

    // Weight range filter (kg/m²) — utile per progettisti che devono rispettare
    // carichi statici solaio e SLEX antisismici.
    if (filters.weightRange && filters.weightRange !== 'all') {
      const weight = Number(stratigraphy.weight_per_sqm ?? 0);
      if (filters.weightRange.endsWith('+')) {
        const min = Number(filters.weightRange.replace('+', ''));
        if (weight < min) return false;
      } else {
        const [min, max] = filters.weightRange.split('-').map(Number);
        if (weight < min || (max && weight > max)) return false;
      }
    }

    // Certified only filter
    if (filters.showCertifiedOnly && !stratigraphy.is_certified) {
      return false;
    }

    // Custom only filter
    if (filters.showCustomOnly && stratigraphy.is_certified) {
      return false;
    }

    return true;
  });
};
